import { Timestamp } from "firebase/firestore";
import { UserSettings } from "./users";

export enum OrganizationStatus {
  TRIAL = "trial",
  PAID = "paid",
  SUSPENDED = "suspended",
}

export enum prospectRunStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

/** Settings > Configuration > Job changes (organization.jobChange). Admins only. */
export interface JobChangeSettings {
  mode: "updateRecord" | "closeAndCreate";
  companyCreation: "create" | "existingOnly";
  /** HubSpot: an hs_lead_status value. Outreach: a stage id. */
  closeOutStatus: string | null;
  closeOutStatusLabel?: string | null;
  /** With existingOnly: add people at companies the CRM does not have, with no company association. */
  createWithoutCompany?: boolean;
  /** Apply a detected job change at once when the new company is already in the CRM. */
  autoApply?: boolean;
  /** Give the record's owner a task to review each detected job change. */
  ownerTask?: boolean;
  updatedAt?: number;
  updatedBy?: string | null;
}

export interface DbOrganizationSettings {
  jobChange?: JobChangeSettings;
  isCustomFieldEnabled?: boolean;
  customField?: number | string | null; // number for Outreach, string (property name) for HubSpot
  disableOptOutProspects?: boolean;
  invalidEmailLeadStatus?: string | null; // HubSpot: lead status to set for contacts with invalid emails
  validEmailLeadStatus?: string | null; // HubSpot: lead status to set when email becomes valid
  isTimestampCustomFieldEnabled?: boolean;
  timestampCustomField?: number | string | null; // number for Outreach, string (property name) for HubSpot
  isEnrichedCustomFieldEnabled?: boolean;
  enrichedCustomField?: number | string | null; // number for Outreach, string (property name) for HubSpot
  // Outreach only: the member urn behind the prospect's LinkedIn profile.
  isLinkedInUrnCustomFieldEnabled?: boolean;
  linkedInUrnCustomField?: number | null;
  // Outreach only: the verdict from checking the number already on the prospect.
  isPhoneStatusCustomFieldEnabled?: boolean;
  phoneStatusCustomField?: number | null;
  isPhoneVerifiedAtCustomFieldEnabled?: boolean;
  /** Verify and enrich on every prospect visit in Outreach. Unset means on. */
  isAutoEnrichOnVisitEnabled?: boolean;
  /** LinkedIn messages, connection requests and post engagement on the contact's CRM timeline. Unset means on. */
  isLinkedInActivityLoggingEnabled?: boolean;
  /** Also log up to 30 days of a conversation's earlier messages when boring first sees it. Off unless set. */
  isLinkedInActivityBackfillEnabled?: boolean;
  isStopSequencesOnLinkedInReplyEnabled?: boolean;
  /** HubSpot: the property that says whose contact it is when not Contact owner (TCB Europe: mda_owner). */
  ownerProperty?: string;
  /** Outreach: boring's own actions (verifications, found emails and numbers, job changes, opt-outs) as cards in the activity feed. Unset means on. */
  isBoringActivityLoggingEnabled?: boolean;
  /** Set once an admin installed boring's Outreach app: activity shows as boring's own cards in the feed. */
  outreachS2SInstallId?: string | null;
  phoneVerifiedAtCustomField?: number | null;
  /** "Changed roles on": the day boring detected a job change. HubSpot: unset means on (boring creates the property). */
  isJobChangeDateCustomFieldEnabled?: boolean;
  jobChangeDateCustomField?: number | string | null; // number for Outreach, string (property name) for HubSpot
  isPhoneCarrierLookupEnabled?: boolean;
  trialDetails?: DbOrganizationTrialDetails;
  isPayingCustomer?: boolean;
  isSegmentedAccount?: boolean;
  isTrial?: boolean;
  isTrialExpired?: boolean;
  billingType?: BillingType;
  billing?: DbOrganizationBilling;
  /** Explicit product tier; overrides the isEnrichmentEnabled derivation. */
  billingPlan?: BillingPlanKey;
  /**
   * Plan gating marker: 2 for organizations created after plan gating, whose
   * features follow the plan. Absent: legacy, everything stays on.
   */
  entitlementsVersion?: number;
  /** Comped grants plan gating still honours (see common/plans.ts). */
  isLinkedinEnrichmentEnabled?: boolean;
  featureFlags?: Partial<Record<string, boolean>>;
  addons?: Partial<Record<string, { status?: string | null } | null>>;
  /** Comped admin seats added on top of the paid Stripe quantity. */
  freeSeats?: number;
  isFinishedOnboarding?: boolean;
  allowedOwnerEmails?: string[];
  allowedOwnerNames?: string[];
  availableLicenses?: number | null;
  licenseSyncTeams?: string[];
  licenseSyncRoles?: string[];
  autoSyncLicensesEnabled?: boolean;
  /** When true (Outreach only): ownership change to whitelisted owner triggers verification. */
  ownerChangeTriggersVerification?: boolean;
  /** When true (HubSpot only): capture sequence info when email invalid, re-enroll after enrichment. Customer's workflow unenrolls. */
  hubspotSequenceReenrollmentEnabled?: boolean;
}

export interface DbOrganizationTrialDetails {
  startedAt: string;
  expiresAt: string;
  /** Legacy no-card trial: org-wide pool. Absent on card-required trials. */
  remainingVerifications?: number;
  /** Card-required trial: per-user verification cap (e.g. 1000). */
  verificationsPerUser?: number;
  /** Card-required trial: usage per owner email ("." replaced by ","). */
  usagePerUser?: Record<string, number>;
}

export type BillingType = "stripe" | "bank_transfer";

export type BillingInterval = "monthly" | "yearly";

/** The plans existing customers bought; not sold to new purchases. */
export type LegacyPlanKey = "verify" | "enrich" | "prospector";
/** The packages sold from 2026-10-23 (website/lib/pricing.ts). */
export type PackagePlanKey = "data" | "linkedin" | "everything" | "dialFaster";
export type BillingPlanKey = LegacyPlanKey | PackagePlanKey;

export interface DbOrganizationBilling {
  subscriptionId?: string | null;
  status?: string | null;
  interval?: BillingInterval | null;
  seats?: number | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  priceId?: string | null;
  /** Effective per-seat amount in cents — what the customer actually pays. */
  unitAmount?: number | null;
  currency?: string | null;
  trialEnd?: string | null;
  trialUsed?: boolean;
  paymentMethodBrand?: string | null;
  paymentMethodLast4?: string | null;
}
export type OrganizationSettings = DbOrganizationSettings;

export interface OrgWebhookStatus {
  active?: boolean;
}

export interface Organization extends OrganizationSettings {
  /** Outreach OAuth scope set the org last authorized with; below current means a reconnect is needed. */
  outreachScopesVersion?: number;
  /** Admin-set: connects with the base Outreach scopes only, so no reconnect prompt. */
  outreachBaseScopesOnly?: boolean;
  id?: string;
  name?: string;
  status?: OrganizationStatus;
  isOutreachAuthorized?: boolean;
  outreachAuthentication?: OutreachAuthentication;
  isHubSpotAuthorized?: boolean;
  hubSpotAuthentication?: HubSpotAuthentication;
  prospectsWebhookId?: number;
  hubSpotWebhookId?: string;
  isEnrichmentEnabled?: boolean;
  initialIntegrationType?: "outreach" | "hubspot";
}

/**
 * The organization fields the public Outreach email report gets from
 * getOrganizationByOutreachOrgId (the server's toEmbedOrganization allowlist).
 */
export type EmbedOrganization = Pick<
  Organization,
  | "id"
  | "isEnrichmentEnabled"
  | "initialIntegrationType"
  | "timestampCustomField"
  | "enrichedCustomField"
  | "customField"
  | "disableOptOutProspects"
>;

export interface HubSpotAuthentication {
  authorizationCode?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  accessTokenExpiresAt?: number;
  refreshTokenExpiresAt?: number;
}

export interface OutreachAuthentication {
  authorizationCode: string;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number; // TODO: deprecated - we should use accessTokenExpiresAt, remove this
  accessTokenExpiresAt?: number;
}

export interface StatProspect {
  prospectId: string;
  email: string;
  emailCheckResult: EmailCheckResult;
}

export interface AccountSettings {
  userSettings: UserSettings;
  organizationSettings: OrganizationSettings;
}

export enum OutreachEmailStatuses {
  Valid = "valid",
  Invalid = "invalid",
  Questionable = "questionable",
  NoEmail = "noEmail",
}

export enum ExtendedEmailStatuses {
  EnrichedByBoring = "enrichedByBoring",
  BouncesPrevented = "bouncesPrevented",
}

export enum GeneralStatuses {
  ProspectsChecked = "prospectsChecked",
}

export type EmailStatuses =
  | OutreachEmailStatuses
  | ExtendedEmailStatuses
  | GeneralStatuses;

export interface OrganizationStats {
  emailsChecked?: number;
  prospectsChecked?: number;
  [ExtendedEmailStatuses.EnrichedByBoring]?: number;
  [ExtendedEmailStatuses.BouncesPrevented]?: number;
  [OutreachEmailStatuses.Valid]?: number;
  [OutreachEmailStatuses.Invalid]?: number;
  [OutreachEmailStatuses.Questionable]?: number;
  [OutreachEmailStatuses.NoEmail]?: number;
  timestamp?: Timestamp;
}

export interface AbstractApiParam {
  text: string;
  value: boolean;
}

export interface AbstractApiResult {
  email: string;
  auto_correct: string;
  deliverability: string;
  quality_score: number;
  is_valid_format: AbstractApiParam;
  is_free_email: AbstractApiParam;
  is_disposable_email: AbstractApiParam;
  is_role_email: AbstractApiParam;
  is_catchall_email: AbstractApiParam;
  is_mx_found: AbstractApiParam;
  is_smtp_valid: AbstractApiParam;
}

export interface EmailCheckResult {
  email: string;
  score: number;
  isLikelyValid?: boolean;
  isSyntaxValid: boolean;
  isBurnerEmail?: boolean;
  isMxFound?: boolean;
  abstractApiResult?: AbstractApiResult;
}
